/**
 * CardForm — ReservePay CARD payment form
 *
 * Collects card details and tokenizes via backend proxy.
 * Used inside the full-page /payment/reservepay?method=card flow.
 */
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2, CreditCard } from 'lucide-react'
import { useReservePay } from '@/src/hooks/useReservePay'

interface CardFormProps {
  amount: number
  onSubmit: (paymentSessionId: string) => void
  onCancel: () => void
}

export function CardForm({ amount, onSubmit, onCancel }: CardFormProps) {
  const { tokenizeCard, selectCardPayment } = useReservePay()

  const [number, setNumber] = useState('')
  const [expiry, setExpiry] = useState('') // MM/YY
  const [cvc, setCvc] = useState('')
  const [name, setName] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function parseExpiry(raw: string): { expMonth: string; expYear: string } | null {
    const digits = raw.replace(/\D/g, '')
    if (digits.length < 4) return null
    const month = digits.slice(0, 2)
    const year = `20${digits.slice(2, 4)}`
    if (!/^(0[1-9]|1[0-2])$/.test(month)) return null
    return { expMonth: month, expYear: year }
  }

  const handleSubmit = async () => {
    setError(null)

    if (!/^\d{13,19}$/.test(number.replace(/\s/g, ''))) {
      setError('กรุณากรอกเลขบัตรให้ถูกต้อง')
      return
    }

    const parsed = parseExpiry(expiry)
    if (!parsed) {
      setError('กรุณากรอกวันหมดอายุให้ถูกต้อง (MM/YY)')
      return
    }
    if (parseInt(parsed.expYear) < new Date().getFullYear()) {
      setError('บัตรหมดอายุแล้ว')
      return
    }

    if (!/^\d{3,4}$/.test(cvc)) {
      setError('กรุณากรอก CVV ให้ถูกต้อง')
      return
    }

    setLoading(true)
    try {
      const cleanNumber = number.replace(/\s/g, '')
      const cardToken = await tokenizeCard({
        number: cleanNumber,
        exp_month: parsed.expMonth,
        exp_year: parsed.expYear,
        cvc,
        name: name || undefined,
      })

      const { paymentSessionId } = await selectCardPayment(amount, cardToken)
      onSubmit(paymentSessionId)
    } catch (err: any) {
      setError(err.message || 'เกิดข้อผิดพลาดในการตรวจสอบบัตร')
    } finally {
      setLoading(false)
    }
  }

  const formatCardNumber = (val: string) => {
    const digits = val.replace(/\D/g, '').slice(0, 16)
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ')
  }

  /** Auto-format MM/YY — inserts slash after 2 digits */
  const formatExpiry = (val: string) => {
    const digits = val.replace(/\D/g, '').slice(0, 4)
    if (digits.length >= 3) return `${digits.slice(0, 2)}/${digits.slice(2)}`
    return digits
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="text-center space-y-1">
        <p className="text-sm text-gray-500">จำนวนเงิน</p>
        <p className="text-3xl font-bold text-[#7FB069]">
          ฿{amount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
        </p>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label>เลขบัตร</Label>
          <Input
            placeholder="1234 5678 9012 3456"
            value={formatCardNumber(number)}
            onChange={(e) => setNumber(e.target.value)}
            maxLength={19}
            disabled={loading}
            className="font-mono tracking-wider"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>วันหมดอายุ (MM/YY)</Label>
            <Input
              placeholder="MM/YY"
              value={formatExpiry(expiry)}
              onChange={(e) => setExpiry(e.target.value)}
              maxLength={5}
              disabled={loading}
              className="font-mono"
            />
          </div>
          <div className="space-y-2">
            <Label>CVV</Label>
            <Input
              placeholder="123"
              type="password"
              value={cvc}
              onChange={(e) => setCvc(e.target.value.slice(0, 4))}
              maxLength={4}
              disabled={loading}
              className="font-mono"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>ชื่อบนบัตร (ไม่จำเป็น)</Label>
          <Input
            placeholder="ชื่อ นามสกุล"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={loading}
          />
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm border border-red-200">
          {error}
        </div>
      )}

      <div className="flex gap-3 pt-2">
        <Button variant="outline" onClick={onCancel} disabled={loading} className="flex-1">
          ยกเลิก
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          className="flex-1 bg-[#7FB069] hover:bg-[#6a9457] text-white"
        >
          {loading ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> กำลังตรวจสอบ...</>
          ) : (
            <><CreditCard className="w-4 h-4 mr-2" /> ชำระ ฿{amount.toLocaleString()}</>
          )}
        </Button>
      </div>
    </div>
  )
}
